import { defineStore, acceptHMRUpdate } from 'pinia';
import { useEnvironment } from '@/composables/useEnvironment';
import { useUsersStore } from './useUsers';
import { metricsApi } from '@/services/metricsService';

const { isLocal } = useEnvironment();
const CLIENT_TIMEZONE = import.meta.env.VITE_CLIENT_TIMEZONE;

// Constants for metric types
const METRIC_TYPES = {
  COMPLETE: 'Complete',
  WIP: 'Wip',
  ASSIGNED: 'Assigned',
};

const createEmptyMetrics = () => {
  const last_saved_projected_pace = sessionStorage.getItem('projected_pace', 0);
  return {
    dailyGoalStreak: 0,
    dailyComplete: 0,
    weeklyComplete: 0,
    monthlyComplete: 0,
    dailyWip: 0,
    weeklyWip: 0,
    monthlyWip: 0,
    dailyAssigned: 0,
    weeklyAssigned: 0,
    monthlyAssigned: 0,
    prevWeeklyComplete: 0,
    prevMonthlyComplete: 0,
    prevWeeklyWip: 0,
    prevMonthlyWip: 0,
    prevWeeklyAssigned: 0,
    prevMonthlyAssigned: 0,
    projected_daily_pace: last_saved_projected_pace,
  };
};

export const useMetrics = defineStore('metrics', {
  state: () => ({
    // Loading states
    isLoading: true,
    isLoadingSelectedUser: true,

    // UI state
    currentTabID: null,
    showMetrics: false,

    holidayForDate: 'None',

    // Cache state
    hasFetchedUserMetrics: false,

    // Active user (logged in user)
    activeUser: {
      username: null,
      id: null,
      metrics: createEmptyMetrics(),
    },

    // Selected user (from users table)
    selectedUser: {
      metrics: createEmptyMetrics(),
    },
  }),

  getters: {
    // Computed metrics for trends and comparisons
    activeUserWeeklyCompleteGrowth: state => {
      const { weeklyComplete, prevWeeklyComplete } = state.activeUser.metrics;
      return prevWeeklyComplete > 0
        ? ((weeklyComplete - prevWeeklyComplete) / prevWeeklyComplete) * 100
        : 0;
    },

    activeUserMonthlyCompleteGrowth: state => {
      const { monthlyComplete, prevMonthlyComplete } = state.activeUser.metrics;
      return prevMonthlyComplete > 0
        ? ((monthlyComplete - prevMonthlyComplete) / prevMonthlyComplete) * 100
        : 0;
    },
  },

  actions: {
    // UI State Management
    setShowMetrics(value) {
      this.showMetrics = value;
    },

    // Active User Management
    setActiveUserID(value) {
      this.activeUser.id = value;
    },

    setActiveUser(username, id) {
      this.activeUser.username = username;
      this.activeUser.id = id;
      // Reset metrics when user changes
      this.activeUser.metrics = createEmptyMetrics();
      this.hasFetchedUserMetrics = false;
    },

    // Utility Methods
    getMetricValue(isActiveUser = true, metricPath) {
      const user = isActiveUser ? this.activeUser : this.selectedUser;
      return metricPath.split('.').reduce((obj, key) => obj?.[key], user) || 0;
    },

    // Private API Methods
    async _fetchUserMetrics(userId) {
      if (!userId) {
        throw new Error('User ID is required');
      }
      const data = await metricsApi.getUserMetrics(userId);
      return data?.metric_value;
    },

    async _updateLocalMetrics() {
      if (!this.activeUser.id) {
        console.warn('No active user ID available for local metrics update');
        return;
      }
      await metricsApi.updateLocalMetrics(
        this.activeUser.id,
        this.activeUser.metrics
      );
    },

    _mapApiMetricsToState(apiMetrics) {
      const fieldMap = {
        dailyGoalStreak: 'current_streak',
        dailyComplete: 'daily_complete',
        weeklyComplete: 'weekly_complete',
        monthlyComplete: 'monthly_complete',
        dailyWip: 'daily_wip',
        weeklyWip: 'weekly_wip',
        monthlyWip: 'monthly_wip',
        dailyAssigned: 'daily_assigned',
        weeklyAssigned: 'weekly_assigned',
        monthlyAssigned: 'monthly_assigned',
        prevWeeklyComplete: 'prev_week_complete',
        prevMonthlyComplete: 'prev_month_complete',
        prevWeeklyWip: 'prev_week_wip',
        prevMonthlyWip: 'prev_month_wip',
        prevWeeklyAssigned: 'prev_week_assigned',
        prevMonthlyAssigned: 'prev_month_assigned',
        projected_daily_pace: 'projected_daily_pace',
      };
      const mapped = {};
      for (const [stateKey, apiKey] of Object.entries(fieldMap)) {
        let value = apiMetrics?.[apiKey] ?? 0;

        if (stateKey === 'projected_daily_pace') {
          value = Math.round(value);
          sessionStorage.setItem('projected_pace', value);
        }
        mapped[stateKey] = value;
      }
      return mapped;
    },

    // Public API Methods
    async fetchActiveUserMetrics() {
      if (this.hasFetchedUserMetrics || !this.activeUser.id) {
        return;
      }

      this.isLoading = true;

      try {
        const apiMetrics = await this._fetchUserMetrics(this.activeUser.id);
        this.activeUser.metrics = this._mapApiMetricsToState(apiMetrics);
        this.hasFetchedUserMetrics = true;
        return this.activeUser.metrics;
      } catch (error) {
        console.error('Failed to fetch active user metrics:', error);
        // Reset to empty metrics on error
        this.activeUser.metrics = createEmptyMetrics();
      } finally {
        this.isLoading = false;
      }
    },

    async fetchSelectedUserMetrics(userId) {
      if (!userId) {
        console.warn('No user ID provided for selected user metrics');
        return;
      }

      this.isLoadingSelectedUser = true;

      try {
        const apiMetrics = await this._fetchUserMetrics(userId);
        this.selectedUser.metrics = this._mapApiMetricsToState(apiMetrics);
        return this.selectedUser.metrics;
      } catch (error) {
        console.error('Failed to fetch selected user metrics:', error);
        // Reset to empty metrics on error
        this.selectedUser.metrics = createEmptyMetrics();
      } finally {
        this.isLoadingSelectedUser = false;
      }
    },

    incrementActiveUserMetric(metricType, incrementBy = 1) {
      // Normalize metric type names
      const normalizedType = this._normalizeMetricType(metricType);

      if (!normalizedType) {
        console.warn(`Unknown metric type: ${metricType}`);
        return;
      }

      const { metrics } = this.activeUser;

      // Increment all time periods for the metric type
      metrics[`daily${normalizedType}`] += incrementBy;
      metrics[`weekly${normalizedType}`] += incrementBy;
      metrics[`monthly${normalizedType}`] += incrementBy;

      const userStore = useUsersStore();

      // Update projection daily pace if completing items
      if (normalizedType === METRIC_TYPES.COMPLETE) {
        //This part is kinda janky but it works

        // daily_login_time is naive timestamp (no tz) so subtract using same client tz
        const currentTime = new Date(
          new Date().toLocaleString('en-US', { timeZone: CLIENT_TIMEZONE })
        );
        const loginTime = new Date(userStore.activeUser.daily_login_time);

        const difference =
          (currentTime.getTime() - loginTime.getTime()) / 3600000;
        const shiftDuration =
          userStore.activeUser.shift_duration.hours +
          userStore.activeUser.shift_duration.minutes / 60;

        if (difference >= shiftDuration) {
          // If the user has been logged in longer than their shift duration, just set pace to completed
          metrics.projected_daily_pace = metrics.dailyComplete;
        } else {
          // This calculation is
          // [( # of work items Completed ) / ( Hours since first login of the day )] x [ Shift Duration ]
          metrics.projected_daily_pace =
            (metrics.dailyComplete / difference) * shiftDuration;
          metrics.projected_daily_pace = Math.round(
            metrics.projected_daily_pace
          );
        }
      }

      // Update local metrics in development mode
      if (isLocal()) {
        this._updateLocalMetrics().catch(error => {
          console.error('Failed to sync local metrics:', error);
        });
      }
    },

    async getHolidayForDate(date) {
      if (!date) {
        date = new Date().toISOString().split('T')[0];
      }
      const data = await metricsApi.getHolidayForDate(date);
      this.holidayForDate = data?.holiday_name || 'None';
      return this.holidayForDate;
    },

    _normalizeMetricType(metricType) {
      const typeMap = {
        Completed: METRIC_TYPES.COMPLETE,
        Complete: METRIC_TYPES.COMPLETE,
        'Added to WIP': METRIC_TYPES.WIP,
        Wip: METRIC_TYPES.WIP,
        WIP: METRIC_TYPES.WIP,
        Assigned: METRIC_TYPES.ASSIGNED,
      };

      return typeMap[metricType] || null;
    },
  },
});

// Enable Hot Module Replacement (HMR)
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useMetrics, import.meta.hot));
}
